/**
 * Viewport renderer (WebGL2). A near-drop-in replacement for the
 * original CanvasGL but with zoom/pan state lifted into the shared
 * `ViewportStore` so the annotation layer can co-register.
 *
 * Window/level on right-drag; pan on primary-drag; wheel zoom with
 * cursor-anchored scaling. Double-click = reset view.
 */
import { createEffect, on, onCleanup, onMount, createSignal } from "solid-js";
import type { DecodedPixels } from "../core/dicom";
import type { ViewportState } from "./store";

// Exported so the StudyViewer HUD can display the current draw tick
// — lets us tell from the UI whether the WebGL texture is actually
// being re-uploaded when the user scrolls through the stack.
export const [canvasDrawTick, setCanvasDrawTick] = createSignal(0);
export const [canvasUploadTick, setCanvasUploadTick] = createSignal(0);

export interface CanvasProps {
  pixels: DecodedPixels;
  viewport: ViewportState;
  windowCenter: number;
  windowWidth:  number;
  /** When set, primary-drag dispatches to the matching gesture. */
  activeTool?: "none" | "pan" | "zoom" | "window"
             | "length" | "angle" | "cobb" | "ellipse" | "rect" | "freehand";
  onWindowChange?: (center: number, width: number) => void;
}

const VS = /* glsl */ `#version 300 es
in  vec2 a_pos;
out vec2 v_uv;
uniform vec4 u_xform;  // (tx, ty, sx, sy) in clip space
uniform vec2 u_flip;   // (1/-1, 1/-1)
void main() {
  v_uv = vec2(a_pos.x * 0.5 + 0.5, 1.0 - (a_pos.y * 0.5 + 0.5));
  v_uv = vec2(u_flip.x > 0.0 ? v_uv.x : 1.0 - v_uv.x,
              u_flip.y > 0.0 ? v_uv.y : 1.0 - v_uv.y);
  vec2 p = vec2(a_pos.x * u_xform.z + u_xform.x,
                a_pos.y * u_xform.w + u_xform.y);
  gl_Position = vec4(p, 0.0, 1.0);
}
`;

const FS_MONO = /* glsl */ `#version 300 es
precision highp float;
in  vec2 v_uv;
out vec4 outColor;
uniform sampler2D u_tex;
uniform float u_slope, u_intercept, u_wc, u_ww, u_invert, u_signed;
void main() {
  vec4 tx = texture(u_tex, v_uv);
  float raw = tx.r * 255.0 + tx.g * 255.0 * 256.0;
  if (u_signed > 0.5 && raw > 32767.0) raw -= 65536.0;
  float hu = raw * u_slope + u_intercept;
  float lo = u_wc - u_ww * 0.5;
  float hi = u_wc + u_ww * 0.5;
  float t  = clamp((hu - lo) / max(hi - lo, 1e-6), 0.0, 1.0);
  if (u_invert > 0.5) t = 1.0 - t;
  outColor = vec4(t, t, t, 1.0);
}
`;
const FS_RGB = /* glsl */ `#version 300 es
precision highp float;
in  vec2 v_uv;
out vec4 outColor;
uniform sampler2D u_tex;
void main() { outColor = vec4(texture(u_tex, v_uv).rgb, 1.0); }
`;

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS))
    throw new Error("shader: " + gl.getShaderInfoLog(sh));
  return sh;
}
function program(gl: WebGL2RenderingContext, vs: string, fs: string) {
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vs));
  gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS))
    throw new Error("link: " + gl.getProgramInfoLog(p));
  return p;
}

export function Canvas(props: CanvasProps) {
  let canvas!: HTMLCanvasElement;
  let container!: HTMLDivElement;

  let gl: WebGL2RenderingContext | null = null;
  let monoProg: WebGLProgram | null = null;
  let rgbProg:  WebGLProgram | null = null;
  let tex: WebGLTexture | null = null;
  let vao: WebGLVertexArrayObject | null = null;
  let lastUpload: DecodedPixels | null = null;

  const v = props.viewport;

  onMount(() => {
    gl = canvas.getContext("webgl2", { preserveDrawingBuffer: false });
    if (!gl) throw new Error("WebGL2 not supported");
    monoProg = program(gl, VS, FS_MONO);
    rgbProg  = program(gl, VS, FS_RGB);

    vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(monoProg, "a_pos");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const ro = new ResizeObserver(() => {
      v.setCanvasSize(container.clientWidth, container.clientHeight);
      draw();
    });
    ro.observe(container);
    v.setCanvasSize(container.clientWidth, container.clientHeight);
    onCleanup(() => ro.disconnect());
  });

  function upload() {
    if (!gl || !tex) return;
    const p = props.pixels;
    if (p === lastUpload) return;
    lastUpload = p;
    setCanvasUploadTick((n) => n + 1);
    v.setImageSize(p.width, p.height);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    if (p.photometric === "RGB") {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, p.width, p.height, 0, gl.RGB, gl.UNSIGNED_BYTE,
                    new Uint8Array(p.rgba.buffer, p.rgba.byteOffset).subarray(0, p.width*p.height*3));
    } else if (p.stored instanceof Uint8Array) {
      const pack = new Uint8Array(p.width * p.height * 2);
      for (let i = 0; i < p.stored.length; i++) pack[i * 2] = p.stored[i];
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RG8, p.width, p.height, 0, gl.RG, gl.UNSIGNED_BYTE, pack);
    } else if (p.stored instanceof Uint16Array || p.stored instanceof Int16Array) {
      const src = p.stored, pack = new Uint8Array(p.width * p.height * 2);
      for (let i = 0; i < src.length; i++) {
        const x = src[i] & 0xffff;
        pack[i * 2] = x & 0xff; pack[i * 2 + 1] = (x >> 8) & 0xff;
      }
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RG8, p.width, p.height, 0, gl.RG, gl.UNSIGNED_BYTE, pack);
    }
  }

  function draw() {
    if (!gl || !monoProg || !rgbProg) return;
    upload();
    const cw = container.clientWidth, ch = container.clientHeight;
    canvas.width = cw; canvas.height = ch;
    gl.viewport(0, 0, cw, ch);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.bindVertexArray(vao);

    const p = props.pixels;
    const scale = v.scale();
    const sx = (p.width  * scale) / cw;
    const sy = (p.height * scale) / ch;
    const tx = (v.pan().x * 2) / cw;
    const ty = -(v.pan().y * 2) / ch;

    const useMono = p.photometric !== "RGB";
    const prog = useMono ? monoProg : rgbProg;
    gl.useProgram(prog);
    gl.uniform4f(gl.getUniformLocation(prog, "u_xform"), tx, ty, sx, sy);
    gl.uniform2f(gl.getUniformLocation(prog, "u_flip"),
                 v.flipH() ? -1 : 1, v.flipV() ? -1 : 1);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.uniform1i(gl.getUniformLocation(prog, "u_tex"), 0);

    if (useMono) {
      gl.uniform1f(gl.getUniformLocation(prog, "u_slope"),     p.rescaleSlope);
      gl.uniform1f(gl.getUniformLocation(prog, "u_intercept"), p.rescaleIntercept);
      gl.uniform1f(gl.getUniformLocation(prog, "u_wc"),        props.windowCenter);
      gl.uniform1f(gl.getUniformLocation(prog, "u_ww"),        Math.max(1, props.windowWidth));
      // Invert = store toggle XOR the image's native polarity.
      const native = p.photometric === "MONOCHROME1";
      const want = v.invert() ? !native : native;
      gl.uniform1f(gl.getUniformLocation(prog, "u_invert"),    want ? 1 : 0);
      gl.uniform1f(gl.getUniformLocation(prog, "u_signed"),    p.stored instanceof Int16Array ? 1 : 0);
    }
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  // Explicit dependency tracking via `on()` — bare expression
  // references (`props.pixels; props.windowCenter;`) were at risk of
  // being pruned by the production minifier even though Solid's
  // compiler keeps them in dev. The `on()` form is unambiguous:
  // Solid wraps each dependency in its own tracking scope, so the
  // effect re-runs whenever any of them changes, in every build.
  createEffect(on(
    [
      () => props.pixels,
      () => props.windowCenter,
      () => props.windowWidth,
      () => v.zoom(),
      () => v.pan(),
      () => v.flipH(),
      () => v.flipV(),
      () => v.invert(),
    ],
    () => {
      setCanvasDrawTick((n) => n + 1);
      draw();
    },
  ));

  // ---------- Input ----------
  // A single "gesture" tracks whichever drag is active. Pan / Zoom /
  // Window / "no drag" are chosen at pointerDown based on the button
  // and the currently-selected tool in the palette.
  type Gesture =
    | { kind: "pan";    x: number; y: number; pan: { x: number; y: number } }
    | { kind: "wl";     x: number; y: number; wc: number; ww: number }
    | { kind: "zoom";   x: number; y: number; startZoom: number; mx: number; my: number }
    | null;
  let gesture: Gesture = null;

  function isMeasureTool(t?: string): boolean {
    return t === "length" || t === "angle" || t === "cobb"
        || t === "ellipse" || t === "rect" || t === "freehand";
  }

  // NOTE: Wheel handling now lives on the viewport *container* in
  // StudyViewer — see the comment there. AnnotationLayer / MaskCanvas
  // can sit on top with pointer-events: auto and block the canvas
  // from receiving the wheel directly, so we rely on event bubbling
  // up to the container for every tool.

  const onDown = (e: PointerEvent) => {
    // If a measurement tool is active, the AnnotationLayer above
    // swallows primary clicks; only right-drag (W/L) + middle-drag
    // (pan) are left to the canvas.
    if (e.button === 2) {
      // Right-drag = always W/L
      canvas.setPointerCapture(e.pointerId);
      gesture = { kind: "wl", x: e.clientX, y: e.clientY,
                  wc: props.windowCenter, ww: props.windowWidth };
      e.preventDefault();
      return;
    }
    if (e.button === 1) {
      // Middle-drag = always pan
      canvas.setPointerCapture(e.pointerId);
      gesture = { kind: "pan", x: e.clientX, y: e.clientY, pan: v.pan() };
      return;
    }
    if (e.button !== 0) return;
    // Primary-click — behaviour depends on activeTool. Alt-override
    // always forces pan for power users.
    const tool = props.activeTool ?? "none";
    if (e.altKey || tool === "pan") {
      canvas.setPointerCapture(e.pointerId);
      gesture = { kind: "pan", x: e.clientX, y: e.clientY, pan: v.pan() };
      return;
    }
    if (tool === "window") {
      canvas.setPointerCapture(e.pointerId);
      gesture = { kind: "wl", x: e.clientX, y: e.clientY,
                  wc: props.windowCenter, ww: props.windowWidth };
      return;
    }
    if (tool === "zoom") {
      canvas.setPointerCapture(e.pointerId);
      const rect = container.getBoundingClientRect();
      gesture = { kind: "zoom", x: e.clientX, y: e.clientY,
                  startZoom: v.zoom(),
                  mx: e.clientX - rect.left, my: e.clientY - rect.top };
      return;
    }
    // Measurement tools are handled by the annotation layer — don't
    // start any gesture here.
  };

  const onMove = (e: PointerEvent) => {
    if (!gesture) return;
    if (gesture.kind === "wl") {
      const ww = Math.max(1, gesture.ww + (e.clientX - gesture.x));
      const wc = gesture.wc - (e.clientY - gesture.y);
      props.onWindowChange?.(wc, ww);
      return;
    }
    if (gesture.kind === "pan") {
      v.setPan({ x: gesture.pan.x + (e.clientX - gesture.x),
                 y: gesture.pan.y + (e.clientY - gesture.y) });
      return;
    }
    if (gesture.kind === "zoom") {
      const dy = e.clientY - gesture.y;
      // Drag up = zoom in, drag down = zoom out (linear 0.5 per 100px)
      const z = Math.max(0.1, Math.min(20, gesture.startZoom * Math.pow(2, -dy / 200)));
      // Anchor to the point where the drag began
      const before = v.screenToImage(gesture.mx, gesture.my);
      v.setZoom(z);
      const after = v.screenToImage(gesture.mx, gesture.my);
      const adx = (after.x - before.x) * v.scale();
      const ady = (after.y - before.y) * v.scale();
      v.setPan({ x: v.pan().x + adx, y: v.pan().y + ady });
      return;
    }
  };
  const onUp = () => { gesture = null; };
  const onDblClick = () => v.reset();

  const cursor = () => {
    const t = props.activeTool ?? "none";
    if (t === "pan")    return "grab";
    if (t === "zoom")   return "zoom-in";
    if (t === "window") return "ns-resize";
    return "default";
  };

  return (
    <div ref={container}
         class="relative flex h-full w-full items-center justify-center bg-ink-0 touch-none"
         style={{ cursor: cursor() }}>
      <canvas ref={canvas}
              class="absolute inset-0 h-full w-full"
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              onDblClick={onDblClick}
              onContextMenu={(e) => e.preventDefault()} />
    </div>
  );
}
